import appStore from 'client/AppStore';
import sessionStore from 'client/Session/SessionStore';
import {Map} from 'immutable';
import React, {createContext, useCallback, useEffect, useState} from 'react';

export enum GlobalNavBehaviorEnum {
  VISIBLE_STICKY = 'visible-sticky',
  VISIBLE_NOT_STICKY = 'visible-not-sticky',
  NOT_VISIBLE = 'not-visible'
}
export const DEFAULT_GLOBAL_NAV_BEHAVIOR = GlobalNavBehaviorEnum.VISIBLE_STICKY;

interface ContextProps {
  navBehavior: GlobalNavBehaviorEnum;
  setDetermineNavBehaviorFunctionForPath: (
    path: string,
    determineNavBehavior: () => GlobalNavBehaviorEnum
  ) => void;
}

export const GlobalNavBehaviorContext = createContext({} as ContextProps);

export const GlobalNavBehaviorConsumer = GlobalNavBehaviorContext.Consumer;

type DetermineNavBehaviorMapType = Map<string, {determineNavBehavior: () => GlobalNavBehaviorEnum}>;

const INITIAL_DETERMINE_NAV_BEHAVIOR_MAP = {
  'assignment/:assignmentId': {
    // Students and teachers both see the global nav inside an assignment (AssignmentPracticeView
    // gates its username bar to the no-nav case). A session-less view (e.g. an LMS launch) hides it.
    determineNavBehavior: () =>
      sessionStore.hasValidSession()
        ? DEFAULT_GLOBAL_NAV_BEHAVIOR
        : GlobalNavBehaviorEnum.NOT_VISIBLE
  },
  '/adaptive/practice/:practiceId': {
    determineNavBehavior: () => GlobalNavBehaviorEnum.NOT_VISIBLE
  },
  '/adaptive/skill/:skillId': {
    determineNavBehavior: () => GlobalNavBehaviorEnum.NOT_VISIBLE
  },
  '/diagnostics/:diagnosticId': {
    determineNavBehavior: () => GlobalNavBehaviorEnum.NOT_VISIBLE
  }
};

/**
 * Segment-wise prefix match of the current pathname against a route pattern. The behavior map is
 * keyed by RR3-style patterns (`assignment/:assignmentId`, `/reports`); the TanStack Router
 * migration (#18533) changed per-segment `route.path`, which broke the old exact `routes[1].path`
 * lookup, so we match against the full pathname instead. `:param` segments are wildcards.
 */
const pathMatchesPattern = (pathname: string, pattern: string): boolean => {
  const toSegments = (value: string) =>
    value
      .replace(/^\/+|\/+$/g, '')
      .split('/')
      .filter(Boolean);
  const pathSegments = toSegments(pathname);
  const patternSegments = toSegments(pattern);
  if (patternSegments.length === 0 || patternSegments.length > pathSegments.length) {
    return false;
  }
  return patternSegments.every(
    (segment, index) => segment.startsWith(':') || segment === pathSegments[index]
  );
};

export const GlobalNavBehaviorProvider = ({children}: PropsWithChildrenRequired) => {
  const [navBehavior, setNavBehavior] = useState(DEFAULT_GLOBAL_NAV_BEHAVIOR);
  const [determineNavBehaviorMap, setDetermineNavBehaviorMap] =
    useState<DetermineNavBehaviorMapType>(Map(INITIAL_DETERMINE_NAV_BEHAVIOR_MAP));

  const setDetermineNavBehaviorFunctionForPath = useCallback(
    (path: string, determineNavBehavior: () => GlobalNavBehaviorEnum) => {
      setDetermineNavBehaviorMap((previousDetermineNavBehaviorMap) =>
        previousDetermineNavBehaviorMap.set(path, {determineNavBehavior})
      );
    },
    []
  );

  useEffect(() => {
    const pathname =
      (appStore.routerProps.location as {pathname?: string} | undefined)?.pathname ?? '';
    let matchedBehavior: GlobalNavBehaviorEnum | undefined;
    determineNavBehaviorMap.forEach((entry, pattern) => {
      if (
        matchedBehavior === undefined &&
        entry &&
        pattern &&
        pathMatchesPattern(pathname, pattern)
      ) {
        matchedBehavior = entry.determineNavBehavior();
      }
    });
    setNavBehavior(matchedBehavior ?? DEFAULT_GLOBAL_NAV_BEHAVIOR);
  }, [appStore.routerProps, determineNavBehaviorMap]);

  return (
    <GlobalNavBehaviorContext.Provider
      value={{navBehavior, setDetermineNavBehaviorFunctionForPath}}
    >
      {children}
    </GlobalNavBehaviorContext.Provider>
  );
};
